import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/styles/professional.css";
import App from "@/App";
import { initNativeShell, isNative, registerPush, deviceSignature } from "@/lib/native";
import { unlockWithBiometricIfNeeded } from "@/lib/biometricAuth";
import { verifyFirebaseConnection } from "@/lib/firebase";
import { api } from "@/lib/api";
import { initOfflineSync } from "@/lib/offlineQueue";
import { applyTextSize } from "@/lib/textSize";
import StartupLoader from "@/components/StartupLoader";
import { useEffect, useState } from "react";

// Apply saved text-size preference before first paint.
applyTextSize();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function startBestEffortServices() {
  // Offline support is valuable, but it must never hold the app behind a
  // full-screen startup state when a browser blocks service-worker access.
  const localPreview = process.env.NODE_ENV === "development" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
  try {
    if ("serviceWorker" in navigator && localPreview) {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => Promise.all(registrations
          .filter((r) => (r.active?.scriptURL || r.waiting?.scriptURL || "").endsWith("/firebase-messaging-sw.js"))
          .map((r) => r.unregister())))
        .catch((e) => console.warn("[sw] local preview worker cleanup failed:", e));
    } else if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" })
        .then(() => console.info("[sw] offline cache + FCM service worker registered"))
        .catch((e) => console.warn("[sw] registration failed:", e));
    }
  } catch (e) { console.warn("[sw] service worker setup failed:", e); }
  try { initOfflineSync(); } catch (e) { console.warn("[offline] sync setup failed:", e); }

  // Firebase is independent of session restoration; JWT sign-in remains usable
  // if Firebase is unavailable.
  try {
    verifyFirebaseConnection()
      .then((h) => h.ok
        ? console.info(`[firebase] ✓ Connected to project "${h.projectId}"`, h)
        : console.warn("[firebase] ✗ Not ready:", h.error))
      .catch((e) => console.warn("[firebase] verify threw:", e));
  } catch (e) { console.warn("[firebase] verify setup threw:", e); }
}

function StartupGate() {
  const [state, setState] = useState("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    let fadeTimer;
    (async () => {
      try {
        // Keep the native setup and biometric gate ahead of AuthProvider so
        // the app never starts restoring a session before Face ID/Touch ID.
        await initNativeShell();
        if (isNative()) {
          const ok = await unlockWithBiometricIfNeeded();
          if (!ok) {
            try { localStorage.removeItem("icard_token"); } catch (_) {}
          }
        }
        startBestEffortServices();
        if (active) {
          setState("fading");
          fadeTimer = setTimeout(() => { if (active) setState("ready"); }, 320);
        }
      } catch (error) {
        console.error("[startup] platform setup failed:", error);
        if (active) setState("error");
      }
    })();
    return () => { active = false; clearTimeout(fadeTimer); };
  }, [attempt]);

  useEffect(() => {
    if (state !== "ready" || !isNative()) return;
    // Push permission/token registration is post-start and best-effort.
    try { registerPush(
      async ({ token, platform }) => {
        try {
          const sig = await deviceSignature();
          await api.post("/notifications/register-device", { token, platform, ...sig });
        } catch (_) { /* backend endpoint is best-effort */ }
      },
      (n) => {
        try { window.dispatchEvent(new CustomEvent("icaard:push", { detail: n })); } catch (_) {}
      }
    ); } catch (e) { console.warn("[push] registration setup failed:", e); }
  }, [state]);

  if (state !== "ready") {
    return <StartupLoader error={state === "error"} fadeOut={state === "fading"} onRetry={() => { setState("loading"); setAttempt((value) => value + 1); }} />;
  }
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<StartupGate />);
